import axios from 'axios'
import cloneDeep from 'lodash.clonedeep'
import { i18n } from '../../../plugins/i18n'
import { defaultRegularHours, defaultHolidayRegularHour, defaultDayBeforeHolidayRegularHour, formatDisplayHours, periodCompareFn } from '../../../modules/regularHour'
import { castAttributesForCamelCase, castAttributesForSnakeCase } from '../../../modules/string_converter'
import { deepCopy } from '../../../modules/deepCopy'
import moment from 'moment'
import generateRandomStr from '../../../modules/generateRandomStr'

export const companiesStoreBusinessHours = {
  namespaced: true,
  state: {
    moreHours: [],
    regularHours: [...defaultRegularHours],
    wdayIndexRegularHours: [],
    holidayRegularHour: null,
    dayBeforeHolidayRegularHour: null,
    regularHourAdditionalDescription: { context: '' },
    regularHolidayAdditionalDescription: { context: '' },
    specialHours: [],
    meta: {
      moreHoursTypes: [],
      moreHoursTypeApiError: null,
    },
    loading: false,
    loadError: false,
  },
  getters: {
    registeredMoreHours(state) {
      return state.moreHours.filter(hour => hour.weeks.some(week => week.periods.length !== 0))
    },
    notRegisteredMoreHours(state, getters) {
      return state.meta.moreHoursTypes.filter(moreHoursType => {
        return !getters.registeredMoreHours
          .map(v => v.gmbHoursTypeId)
          .includes(moreHoursType.gmbHoursTypeId)
      })
    },
    displayRegularHours(state) {
      return formatDisplayHours(state.regularHours)
    },
    isRegularHourSet(state) {
      return state.regularHours.filter(v => v.periods.length === 0).length !== state.regularHours.length
    },
    // 日付跨ぎに対応した表示用データを返す
    displayMoreHours(state, getters) {
      return getters.registeredMoreHours.map(unsortedHour => {
        const hour = cloneDeep(unsortedHour)
        hour.weeks = formatDisplayHours(hour.weeks).filter(v => v.periods.length !== 0)
        return hour
      })
    },
    futureSpecialHours(state) {
      return state.specialHours.filter(v => moment(v.date).isSameOrAfter(moment().startOf('day')))
    },
    pastSpecialHours(state) {
      return state.specialHours
        .filter(v => moment(v.date).isBefore(moment().startOf('day')))
        .sort((a, b) => moment(a.date).isAfter(moment(b.date)) ? -1 : 1)
    },
    regularHours(state) {
      return state.regularHours
    },
    specialHours(state) {
      return state.specialHours
    },
    moreHours(state) {
      return state.moreHours
    },
    wdayIndexRegularHours(state) {
      return state.wdayIndexRegularHours
    },
    regularHourAdditionalDescription(state) {
      return state.regularHourAdditionalDescription
    },
    regularHolidayAdditionalDescription(state) {
      return state.regularHolidayAdditionalDescription
    },
    holidayRegularHour(state) {
      return state.holidayRegularHour
    },
    dayBeforeHolidayRegularHour(state) {
      return state.dayBeforeHolidayRegularHour
    },
  },
  mutations: {
    resetData(state) {
      state.regularHours = [...defaultRegularHours]
      state.specialHours = []
      state.moreHours = []
    },
    readStoreBusinessHours(state) {
      state.regularHours = [...defaultRegularHours]
      state.specialHours = []
      state.moreHours = []
      state.loading = true
      state.loadError = false
    },
    readStoreBusinessHoursSuccess(state, { payload }) {
      const data = castAttributesForCamelCase(payload.data)
      if (data.regularHours.length === 0) {
        state.regularHours = [...deepCopy(defaultRegularHours)]
      } else {
        state.regularHours = state.regularHours
          .map(hour => {
            const newHour = data.regularHours.find(v => v.wday === hour.wday) ?? hour
            newHour.periods.sort(periodCompareFn)
            return newHour
          })
      }
      if (data.holidayRegularHour) {
        data.holidayRegularHour.periods.sort(periodCompareFn)
      }
      state.holidayRegularHour = data.holidayRegularHour

      if (data.dayBeforeHolidayRegularHour) {
        data.dayBeforeHolidayRegularHour.periods.sort(periodCompareFn)
      }
      state.dayBeforeHolidayRegularHour = data.dayBeforeHolidayRegularHour

      state.wdayIndexRegularHours = data.wdayIndexRegularHours.map(v => {
        v.periods.sort(periodCompareFn)
        return v
      })
      state.regularHourAdditionalDescription.context = data.regularHourAdditionalDescription ? data.regularHourAdditionalDescription.context : ''
      state.regularHolidayAdditionalDescription.context = data.regularHolidayAdditionalDescription ? data.regularHolidayAdditionalDescription.context : ''
      state.specialHours = data.specialHours.map(specialHour => {
        specialHour['specialHourUId'] = `original-${generateRandomStr(1000)}`
        return specialHour
      })
      state.moreHours = data.moreHours.map(v => {
        v.weeks = defaultRegularHours.map(hour => {
          const index = v.weeks.findIndex(week => hour.wday === week.wday)
          return index === -1 ? hour : v.weeks[index]
        })
        return castAttributesForCamelCase(v)
      })
      const meta = castAttributesForCamelCase(payload.meta)
      state.meta = {
        moreHoursTypes: meta.moreHoursTypes,
        moreHoursTypeApiError: meta.moreHoursTypeApiError,
      }

      state.loading = false
      state.loadError = false
    },
    readStoreBusinessHoursFailure(state) {
      state.loading = false
      state.loadError = true
    },
    updateStoreBusinessHours(state) {
      state.loading = true
    },
    updateStoreBusinessHoursSuccess(state) {
      state.loading = false
    },
    updateStoreBusinessHoursFailure(state) {
      state.loading = false
    },
    addStoreSpecialHour(state, value) {
      state.specialHours.push(value)
    },
    removeStoreSpecialHour(state, key) {
      state.specialHours.splice(key, 1)
    },
    removeAllStoreSpecialHour(state) {
      state.specialHours = []
    },
    removeAllStoreMoreHour(state) {
      state.moreHours.forEach(hour => {
        hour.weeks.forEach(week => {
          week.periods = []
        })
      })
    },
    addStoreMoreHour(state, selectedMoreHour) {
      const moreHourIndex = state.moreHours.findIndex(hour => hour.gmbHoursTypeId === selectedMoreHour.gmbHoursTypeId)
      if (moreHourIndex >= 0) {
        state.moreHours[moreHourIndex] = cloneDeep(selectedMoreHour)
      } else {
        state.moreHours.push(cloneDeep(selectedMoreHour))
      }
    },
    removeStoreMoreHour(state, gmbHoursTypeId) {
      const moreHourIndex = state.moreHours.findIndex(hour => hour.gmbHoursTypeId === gmbHoursTypeId)
      state.moreHours[moreHourIndex].weeks = [...defaultRegularHours]
    },
    addWdayIndexRegularHours(state) {
      state.wdayIndexRegularHours.push({ isNotSet: true, periods: [], indexes: [], wdays: []})
    },
    removeWdayIndexRegularHour(state, index) {
      state.wdayIndexRegularHours.splice(index, 1)
    },
    setInitialData(state, type) {
      switch (type) {
      case 'holiday':
        state.holidayRegularHour = defaultHolidayRegularHour
        break
      case 'dayBeforeHoliday':
        state.dayBeforeHolidayRegularHour = defaultDayBeforeHolidayRegularHour
        break
      }
    }
  },
  actions: {
    resetData({ commit }) {
      commit('resetData')
    },
    removeStoreSpecialHour({ commit }, key) {
      commit('removeStoreSpecialHour', key)
    },
    addStoreMoreHour({ commit }, selectedMoreHour) {
      commit('addStoreMoreHour', selectedMoreHour)
    },
    removeStoreMoreHour({ commit }, gmbHoursTypeId) {
      commit('removeStoreMoreHour', gmbHoursTypeId)
    },
    removeAllStoreMoreHour({ commit }) {
      commit('removeAllStoreMoreHour')
    },
    addStoreSpecialHour({ commit }, value) {
      commit('addStoreSpecialHour', value)
    },
    removeAllStoreSpecialHour({ commit }) {
      commit('removeAllStoreSpecialHour')
    },
    addWdayIndexRegularHours({ commit }) {
      commit('addWdayIndexRegularHours')
    },
    removeWdayIndexRegularHour({ commit }, index) {
      commit('removeWdayIndexRegularHour', index)
    },
    setInitialData({ commit }, type) {
      commit('setInitialData', type)
    },
    readStoreBusinessHours({ commit }, { company_uuid, store_uuid }) {
      commit('readStoreBusinessHours')
      return axios
        .get('/api/companies/' + company_uuid + '/stores/' + store_uuid + '/business_hours.json')
        .then(res => {
          commit('readStoreBusinessHoursSuccess', { payload: res.data })
        })
        .catch(err => {
          commit('readStoreBusinessHoursFailure')
          commit('flash/updateFlash', { type: 'alert', message: err.response.data.errors[0].detail }, { root: true })
          console.error(err)
        })
    },
    updateStoreBusinessHours({ commit, state }, { regularHours, holidayRegularHour, dayBeforeHolidayRegularHour, wdayIndexRegularHours, regularHourAdditionalDescription, regularHolidayAdditionalDescription, specialHours, moreHours, synced, companyUuid, storeUuid }) {
      if (state.loading) return

      commit('flash/clearFlash', null, { root: true })
      commit('updateStoreBusinessHours')
      const storeBusinessHour = {
        regular_hours: regularHours.map(v => {
          delete v.isNotSet
          delete v.noChange
          return v
        }),
        wday_index_regular_hours: wdayIndexRegularHours.filter(v => !v.isNotSet).map(v => {
          delete v.isNotSet
          delete v.noChange
          return v
        }),
        regular_hour_additional_description: regularHourAdditionalDescription,
        regular_holiday_additional_description: regularHolidayAdditionalDescription,
        special_hours: specialHours,
        more_hours: moreHours,
      }
      if (!holidayRegularHour.isNotSet) {
        delete holidayRegularHour.isNotSet
        delete holidayRegularHour.noChange
        storeBusinessHour['holiday_regular_hour'] = holidayRegularHour
      }
      if (!dayBeforeHolidayRegularHour.isNotSet) {
        delete dayBeforeHolidayRegularHour.isNotSet
        delete dayBeforeHolidayRegularHour.noChange
        storeBusinessHour['day_before_holiday_regular_hour'] = dayBeforeHolidayRegularHour
      }
      const payload = {
        store_business_hours: storeBusinessHour,
        store_gmb_location: {
          business_hours_synced: synced
        }
      }
      axios
        .put('/api/companies/' + companyUuid + '/stores/' + storeUuid + '/business_hours.json', castAttributesForSnakeCase(payload))
        .then(() => {
          commit('updateStoreBusinessHoursSuccess')
          commit('flash/updateFlash', { type: 'notice', message: i18n.t('messages.updated', { value: i18n.t('models.storeRegularHour.modelName') }) }, { root: true })
        })
        .catch(err => {
          commit('updateStoreBusinessHoursFailure')
          commit('flash/updateFlash', { type: 'alert', message: err.response.data.errors[0].detail }, { root: true })
          console.error(err)
        })
    },
  }
}
